import React, { createContext, useContext, useState, useEffect } from 'react';
import engTranslations from '../locales/en.json';
import arTranslations from '../locales/ar.json';

const TranslationContext = createContext();

export const useTranslation = () => {
  const context = useContext(TranslationContext);
  if (!context) {
    throw new Error('useTranslation must be used within a TranslationProvider');
  }
  return context;
};

export const TranslationProvider = ({ children }) => {
  const [language, setLanguage] = useState('eng');
  const [translations, setTranslations] = useState(engTranslations);

  // Function to get language from URL parameters
  const getLanguageFromURL = () => {
    const urlParams = new URLSearchParams(window.location.search);
    const langParam = urlParams.get('lang');
    return langParam === 'ar' ? 'ar' : 'eng';
  };

  // Initialize language from URL on component mount
  useEffect(() => {
    const urlLanguage = getLanguageFromURL();
    setLanguage(urlLanguage);
  }, []);

  // Listen for URL changes (for SPA navigation)
  useEffect(() => {
    const handleURLChange = () => {
      const urlLanguage = getLanguageFromURL();
      setLanguage(urlLanguage);
    };

    // Listen for popstate events (back/forward navigation)
    window.addEventListener('popstate', handleURLChange);
    
    // Also check on initial load
    handleURLChange();

    return () => {
      window.removeEventListener('popstate', handleURLChange);
    };
  }, []);

  useEffect(() => {
    setTranslations(language === 'eng' ? engTranslations : arTranslations);
  }, [language]);

  const toggleLanguage = () => {
    const newLang = language === 'eng' ? 'ar' : 'eng';
    setLanguage(newLang);
    updateURLWithLanguage(newLang);
  };

  // Function to update URL with language parameter (only when manually toggled)
  const updateURLWithLanguage = (newLanguage) => {
    const url = new URL(window.location);
    if (newLanguage === 'ar') {
      url.searchParams.set('lang', 'ar');
    } else {
      url.searchParams.delete('lang');
    }
    window.history.replaceState({}, '', url);
  };

  const t = (key) => {
    const keys = key.split('.');
    let value = translations;
    
    for (const k of keys) {
      if (value && typeof value === 'object' && k in value) {
        value = value[k];
      } else {
        console.warn(`Translation key "${key}" not found`);
        return key;
      }
    }
    
    return value;
  };

  const value = {
    language,
    setLanguage,
    toggleLanguage,
    t,
    isRTL: language === 'ar'
  };

  return (
    <TranslationContext.Provider value={value}>
      {children}
    </TranslationContext.Provider>
  );
};
